import * as React from 'react';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { withTranslation } from 'react-i18next';
import { useReceivablesReport } from '../hooks/useReportsApi';
import { paymentMethodLabel } from '../../../utils/paymentMethods';
import { formatEuro } from '../../../utils/vat';

// 2026-08-31 -> 31.8.2026
const formatIsoDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${parseInt(d, 10)}.${parseInt(m, 10)}.${y}`;
};

/**
 * Avoimet laskut ja suoritukset, the kirjanpitoraportti's third sub-tab: the invoices open on
 * the month's last day and the payments received during the month, both by payment date.
 * Totals here; the invoice and payment lists are in the PDF and Excel (export buttons in the
 * sub-tab row of AccountingTab).
 */
function ReceivablesSection({ t, year, month }) {
  const { data, loading, error } = useReceivablesReport(year, month);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.receivables.rowsNote')}
      </Typography>

      {/* Open invoices on the month's last day (today while the month runs) */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, bgcolor: data?.open_invoices_count > 0 ? 'warning.50' : 'background.paper' }}
        data-testid="receivables-open-invoices"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {data?.open_invoices_count > 0 && <WarningIcon color="warning" />}
          <Typography variant="h6" sx={{ fontWeight: 600 }} data-testid="receivables-open-heading">
            {loading || !data
              ? <Skeleton variant="text" width={220} />
              : t('reports.receivables.openInvoicesAsOf', { date: formatIsoDate(data.open_invoices_as_of) })}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('reports.receivables.openInvoicesNote')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={80} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('reports.receivables.openInvoicesCount')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }} data-testid="receivables-open-count">
                {data?.open_invoices_count || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('reports.receivables.openInvoicesTotal')}
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: data?.open_invoices_total > 0 ? 'warning.main' : 'text.primary' }}
                data-testid="receivables-open-total"
              >
                {formatEuro(data?.open_invoices_total || 0)}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Payments received during the month, every method, by payment date */}
      <Paper elevation={0} sx={{ p: 3 }} data-testid="receivables-payments">
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
          {t('reports.receivables.paymentsReceived')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('reports.receivables.paymentsReceivedNote')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : (data?.payments_by_method || []).length === 0 ? (
          <Typography color="text.secondary">{t('reports.receivables.paymentsNone')}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.receivables.paymentMethod')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.receivables.paymentsCount')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.receivables.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.payments_by_method.map((item) => (
                  <TableRow key={item.payment_method} data-testid={`receivables-payments-row-${item.payment_method}`}>
                    <TableCell>{paymentMethodLabel(t, item.payment_method)}</TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                    <TableCell align="right">{formatEuro(item.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t('reports.receivables.total')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{data.payments_count}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }} data-testid="receivables-payments-total">
                    {formatEuro(data.payments_total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(ReceivablesSection);
